<template>
  <div :id="folderHtmlElementId"
    :class="['filter-view-folder',
      { 'view-menu-open': isButtonActionMenuOpen },
      { 'no-hover': isHoverStyleDisabled && !isButtonActionMenuOpen },
      { 'dragover': isDraggableTarget}]"
      @drop.prevent="handleViewDrop"
      @dragover.prevent="handleViewDragOver"
      @dragleave.prevent="handleViewDragLeave"
      @dragenter.prevent
      @drag.prevent>
    <mc-expandable-section 
      :title="folder.name"
      :title-icon="isExpanded ? 'icon-folder-open' : 'icon-folder-outline'"
      show-title-tooltip
      state-icon="icon-chevron-right"
      :expanded="isExpanded"
      :active-header-state="containsActiveView || isButtonActionMenuOpen"
      clickable-title
      @collapse="collapse"
      @expand="expand"
      @click:right:header="handleFolderHeaderClickRight"
      @mousemove:header="handleFolderHeaderMouseMove"
      @mouseleave:header="handleFolderHeaderMouseLeave"
    >
      <template #actionButton>
        <mc-menu ref="buttonActionMenu" 
          :items="FILTER_VIEW_FOLDER_MENU_ITEMS"
          :placement="actionMenuPlacement"
          @click:item="handleFolderMenuItemClick($event)"
          @close="handleFolderMenuClose">
        </mc-menu>
      </template>
    
      <div v-if="!views.length" class="no-items-message">
        To add a view to this group, hover over a View, press ‘ <span class="message-icon">&#8942;</span> ’ and select ‘move to’
      </div>

      <div v-else class="filter-view-items">
        <filter-view-item v-for="view in views" :key="view.id"
          :view="view"
          :is-selected="selectedViewId === view.id"
          draggable
          @click:apply="handleViewApplyClick"
          @click:menu-item="handleViewMenuItemClick"/>
      </div> 
    </mc-expandable-section>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import _ from 'lodash';
import McMenu from '@/components/ui/McMenu/McMenu.vue';
import McExpandableSection from '@/components/ui/McExpandableSection.vue';
import FilterViewItem from '@/components/shared/filters/FilterView/FilterViewItem.vue';
import { MENU_CLOSE_DELAY, ACTION_MENU_PLACEMENT_DEFAULT, ACTION_MENU_PLACEMENT_RIGHT_CLICK } from '@/constants/menu';
import { FILTER_VIEW_FOLDER_MENU_ITEMS } from '@/constants/quickFilter';
import { getViewFolderHtmlElementId } from '@/helpers/filterView';
import { IMenuItemEvent } from '@/models/menu.model';
import { 
  FilterViewItemType,
  FilterViewItemActionType, 
  IFilterViewMenuItemEvent, 
  IQuickFilterFolderViewModel, 
  IQuickFilterViewModel } from '@/models/quickFilter.model';

interface IFilterViewFolderProps {
  folder: IQuickFilterFolderViewModel;
  views?: IQuickFilterViewModel[];
  selectedViewId?: string;
  expanded?: boolean;
}

const props = withDefaults(defineProps<IFilterViewFolderProps>(), {
  views: () => [] as IQuickFilterViewModel[],
  selectedViewId: undefined,
  expanded: false,
});

const { folder, views, selectedViewId, expanded } = toRefs(props);

const folderHtmlElementId = computed(() => getViewFolderHtmlElementId(folder.value.id));
const containsActiveView = computed(() => views.value.some((view) => view.id === selectedViewId.value));
const isExpanded = ref(expanded.value || containsActiveView.value);
// Used when displaying tooltips.
// Since the tooltip opens with a delay, it is necessary to know whether the course is in the view item element area
const isCursorOverViewItem = ref(false);
// This parameter is used to disable the hover style when there is an item with an open menu in the list
const isHoverStyleDisabled = ref(false);
const isDraggableTarget = ref(false);

const buttonActionMenu = ref();
const isButtonActionMenuOpen = computed(() => buttonActionMenu.value?.isMenuOpen);
const actionMenuPlacement = ref(_.cloneDeep(ACTION_MENU_PLACEMENT_DEFAULT));

const emit = defineEmits([
  'click:menuItem',
  'click:view:apply',
  'click:view:menuItem'
]);

const collapse = () => {
  isExpanded.value = false;
};

const expand = () => {
  isExpanded.value = true;
};

const handleFolderMenuItemClick = (event: IMenuItemEvent) => {
  const actionType = event.value as FilterViewItemActionType;
  if (!actionType) return;

  const eventData = {
    data: folder.value,
    actionItemType: FilterViewItemType.folder,
    actionType: actionType,
  };

  emit('click:menuItem', eventData);
};

const handleFolderHeaderClickRight = () => {
  if (buttonActionMenu.value?.isMenuOpen) {
    buttonActionMenu.value?.closeMenu();
  }
  else {
    buttonActionMenu.value?.openMenu();
    actionMenuPlacement.value = _.cloneDeep(ACTION_MENU_PLACEMENT_RIGHT_CLICK);
  }
};

const handleFolderHeaderMouseMove = () => {
  setMouseMoveAttributes(true);
};

const handleFolderHeaderMouseLeave = () => {
  setMouseMoveAttributes();

  setTimeout(
    () => {
      if (!isCursorOverViewItem.value && isButtonActionMenuOpen.value) {
        buttonActionMenu.value?.closeMenu();
      }
    },
    MENU_CLOSE_DELAY
  );
};

const handleFolderMenuClose = () => {
  actionMenuPlacement.value = _.cloneDeep(ACTION_MENU_PLACEMENT_DEFAULT);
};

const handleViewDrop = (event: DragEvent) => {
  isDraggableTarget.value = false;

  const data = event?.dataTransfer?.getData('text/plain');
  if (!data) return;

  const view = JSON.parse(data) as IQuickFilterViewModel;
  if (!view || view.folderId === folder.value.id) return;

  view.folderId = folder.value.id;

  const eventData = {
    data: view,
    actionItemType: FilterViewItemType.view,
    actionType: FilterViewItemActionType.move,
  };

  emit('click:view:menuItem', eventData);
};

const handleViewDragOver = () => {
  isDraggableTarget.value = true;
};

const handleViewDragLeave = () => {
  isDraggableTarget.value = false;
};

const handleViewApplyClick = (event: IQuickFilterViewModel) => {
  emit('click:view:apply', event);
};

const handleViewMenuItemClick = (event: IFilterViewMenuItemEvent) => {
  emit('click:view:menuItem', event);
};

const setMouseMoveAttributes = (isCursorOverElement: boolean = false) => {
  isCursorOverViewItem.value = isCursorOverElement;
  isHoverStyleDisabled.value = document.querySelectorAll('div.view-menu-open').length > 0;
};

watch(
  () => expanded.value,
  () => isExpanded.value = expanded.value
);

defineExpose({
  collapse,
  expand,
  folderHtmlElementId,
  isExpanded
});

</script>

<style lang="scss" scoped>
.filter-view-folder {
  --expandable-section-header-height: 34px;
  --expandable-section-header-padding: 10px 8px 10px 2px;
  --expandable-section-header-hover-padding: 10px 4px 10px 2px;
  --expandable-section-header-border-radius: 8px;
  --expandable-section-header-background-color-hover: var(--colour-background-neutral-hover-a-dark);
  --expandable-section-header-background-color-active: var(--colour-background-neutral-default-a-dark);
  --expandable-section-state-icon-wrapper-background-color: transparent;
  --expandable-section-state-icon-wrapper-size: 24px;
  --expandable-section-state-icon-wrapper-margin: 0 4px 0 0;
  --expandable-section-state-icon-font-size: 24px;
  --expandable-section-state-icon-font-weight: var(--font-weight-body-medium);
  --expandable-section-state-icon-color: var(--colour-icons-contrast);
  --expandable-section-state-icon-expanded-transform: rotate(90deg);
  --expandable-section-title-icon-color: var(--colour-icons-contrast);
  --expandable-section-title-icon-font-size: 14px;
  --expandable-section-title-icon-font-weight: var(--font-weight-body-regular);
  --expandable-section-title-icon-padding: 0 8px;
  --expandable-section-title-font-color: var(--colour-text-neutral-default-dark);
  --expandable-section-title-font-size: var(--font-size-headers-x-small);
  --expandable-section-title-font-weight: var(--font-weight-body-regular);
  --expandable-section-content-height: unset;
  --expandable-section-content-padding: 2px 0 4px 8px;
  
  width: 100%;
  border: 1px solid transparent;

  .no-items-message {
    color: var(--colour-text-neutral-subtle-dark);
    font-size: var(--font-size-body-small);
    font-style: normal;
    font-weight: var(--font-weight-body-regular);
    line-height: 140%;
    padding: 4px;

    .message-icon {
      font-size: 16px;
      font-weight: var(--font-weight-body-medium);
      vertical-align: bottom;
    }
  }

  &.no-hover {
    :deep(.header-content):hover {
      padding: var(--expandable-section-header-padding);
      background-color: transparent;

      .action-button-wrapper {
        display: none;
      }
    }
  }

  &.view-menu-open,
  &:hover:not(.no-hover) {
    :deep(.header-content) {
      padding: var(--expandable-section-header-hover-padding);
      background-color: var(--expandable-section-header-background-color-hover);

      .action-button-wrapper {
        display: flex;
      }
    }
  }

  .filter-view-items {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-left: 5px;
    padding-left: 13px;
    border-left: 1px solid var(--base-white-alpha-dark-5);
  }

  &.dragover {
    border-radius: 8px;
    border: 1px solid var(--colour-icons-disabled-dark);
    background-color: var(--expandable-section-header-background-color-hover) !important;
  }
}
</style>